import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { formatMembershipDuration, formatDate } from "../packagesHelpers";

function MembershipRow({ membership, opt, currency, onBuy, isBuying, owned }) {
  const price = Number.isFinite(opt.price) ? opt.price : parseFloat(opt.price) || 0;
  const duration = formatMembershipDuration(opt);

  return (
    <Card
      variant="outlined"
      sx={
        owned
          ? {
              borderColor: "primary.main",
              borderWidth: 1.5,
              backgroundColor: (theme) => `${theme.palette.primary.main}0A`
            }
          : undefined
      }
    >
      <CardContent
        sx={{
          display: "flex",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          alignItems: { xs: "flex-start", sm: "center" },
          gap: { xs: 1.5, sm: 2 },
          py: { xs: 2, sm: 2.5 },
          px: { xs: 2, sm: 3 },
          "&:last-child": { pb: { xs: 2, sm: 2.5 } }
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
          <Box
            sx={{
              width: "2.5rem",
              height: "2.5rem",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: (theme) => `${theme.palette.primary.main}14`,
              color: "primary.main"
            }}
          >
            <WorkspacePremiumOutlinedIcon sx={{ fontSize: "1.375rem" }} />
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: { xs: "-webkit-box", sm: "block" },
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: { xs: 2, sm: "unset" },
              whiteSpace: { xs: "normal", sm: "nowrap" }
            }}
          >
            {membership.name}
          </Typography>
          {duration && (
            <Typography variant="body2" color="textSecondary">
              {duration}
            </Typography>
          )}
          {owned && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
              <CheckCircleIcon sx={{ fontSize: "0.9375rem", color: "primary.main" }} />
              <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 700 }}>
                Active membership{owned.noExpiry ? " · No expiry" : owned.expiryDate ? ` · Expires ${formatDate(owned.expiryDate)}` : ""}
              </Typography>
            </Box>
          )}
          {membership.description && (
            <Typography variant="caption" color="textSecondary" sx={{ display: "block", mt: 0.5 }}>
              {membership.description}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexShrink: 0,
            ml: { sm: "auto" },
            flexBasis: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "space-between", sm: "flex-start" },
            mt: { xs: 0.5, sm: 0 },
            pt: { xs: 1.5, sm: 0 },
            borderTop: { xs: "1px solid", sm: "none" },
            borderColor: "divider"
          }}
        >
          <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, fontSize: { xs: "1.0625rem", sm: "1.25rem" } }}>
              {currency} {price.toFixed(2)}
            </Typography>
          </Box>
          <Button
            variant="contained"
            disabled={isBuying}
            onClick={() => onBuy(membership, opt)}
            sx={{ textTransform: "none", color: "common.white", minWidth: "5.625rem" }}
          >
            {isBuying ? <CircularProgress size={18} /> : "Buy"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

export default MembershipRow;
